import React, { useMemo } from "react";
import { Box, Stack, Button, Typography, Divider } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFDownloadLink,
  PDFViewer,
} from "@react-pdf/renderer";

const xmlToJs = (xmlString) => {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlString, "application/xml");

  const err = xml.getElementsByTagName("parsererror")[0];
  if (err) throw new Error("Invalid XML");

  const nodeToValue = (node) => {
    const children = Array.from(node.children || []);
    if (!children.length) {
      const text = (node.textContent || "").trim();
      return text === "" ? "" : text;
    }

    const out = {};
    for (const child of children) {
      const key = child.tagName;
      const val = nodeToValue(child);
      if (out[key] === undefined) out[key] = val;
      else if (Array.isArray(out[key])) out[key].push(val);
      else out[key] = [out[key], val];
    }
    return out;
  };

  const report = xml.getElementsByTagName("REPORT")[0];
  if (!report) {
    return nodeToValue(xml.documentElement);
  }
  return nodeToValue(report);
};

const ensure = (v, dash = "—") =>
  v === null || v === undefined || String(v).trim() === "" ? dash : String(v);

const toArray = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);

const prettyKey = (k) =>
  String(k).replace(/_/g, " ").replace(/\s+/g, " ").trim().toUpperCase();

const joinParts = (parts) =>
  parts
    .map((part) => ensure(part, ""))
    .filter((part) => part && part !== "—")
    .join(", ");

const joinInline = (parts, separator = " | ") =>
  parts
    .map((part) => ensure(part, ""))
    .filter((part) => part && part !== "—")
    .join(separator);

const companyReportSections = (data = {}) => {
  const registry = data.REGISTRY_DETAIL || {};
  const gst = data.GST || {};
  const nameChanges = toArray(data.LIST_NAME_CHANGE?.NAME_CHANGE);
  const capital = toArray(data.LIST_CAPITAL?.CAPITAL);
  const officers = toArray(data.LIST_OFFICER_OWNER?.OFFICER_OWNER);
  const shareholders = toArray(data.LIST_SHAREHOLDER?.SHAREHOLDER);
  const charges = toArray(data.LIST_REG_CHARGES?.REG_CHARGES);
  const liquidators = toArray(data.LIST_LIQUIDATOR?.LIQUIDATOR);
  const litigation = toArray(data.LIST_LITIGATION?.LITIGATION);
  const windingUp = toArray(data.LIST_WINDING_UP?.WINDING_UP);

  return {
    gst,
    nameChanges,
    capital,
    officers,
    shareholders,
    charges,
    liquidators,
    litigation,
    windingUp,
  };
};

const buildCompanyTables = (data = {}) => {
  const {
    registry,
    gst,
    nameChanges,
    capital,
    officers,
    shareholders,
    charges,
    liquidators,
    litigation,
    windingUp,
  } = companyReportSections(data);

  return [
    {
      title: "GST",
      columns: [
        "GST REGISTRATION NUMBER",
        "GST REGISTERED FROM",
        "GST REGISTERED TO",
        "GST STATUS",
      ],
      rows: [
        {
          "GST REGISTRATION NUMBER": ensure(gst.GST_REGISTRATION_NUMBER, "N/A"),
          "GST REGISTERED FROM": ensure(gst.GST_REGISTERED_FROM, "N/A"),
          "GST REGISTERED TO": ensure(gst.GST_REGISTERED_TO, "N/A"),
          "GST STATUS": ensure(gst.GST_STATUS, "N/A"),
        },
      ],
    },
    {
      title: "CHANGE OF COMPANY NAME",
      columns: ["PREVIOUS NAME", "EFFECTIVE DATE"],
      rows: nameChanges.length
        ? nameChanges.map((row) => ({
            "PREVIOUS NAME": ensure(row.PREV_NAME, "Nil"),
            "EFFECTIVE DATE": ensure(row.EFFECT_DATE),
          }))
        : [{ "PREVIOUS NAME": "Nil", "EFFECTIVE DATE": "—" }],
    },
    {
      title: "CAPITAL",
      columns: ["CAPITAL CATEGORY", "CURRENCY", "CAPITAL AMOUNT", "NO. OF SHARES"],
      rows: capital.length
        ? capital.map((row) => ({
            "CAPITAL CATEGORY": ensure(row.CAPITAL_CATEGORY),
            CURRENCY: ensure(row.CURRENCY),
            "CAPITAL AMOUNT": ensure(row.CAPITAL_AMT),
            "NO. OF SHARES": ensure(row.NO_OF_SHARES),
          }))
        : [
            {
              "CAPITAL CATEGORY": "—",
              CURRENCY: "—",
              "CAPITAL AMOUNT": "—",
              "NO. OF SHARES": "—",
            },
          ],
    },
    {
      title: "OFFICER(S) / OWNER(S)",
      columns: [
        "OFFICER NAME / ADDRESS / CHANGE ADDRESS DATE",
        "IDENTITY NO. / PA REG. NO.",
        "POSITION",
        "APPOINTMENT DATE / DISQUALIFIED DATE",
        "NATIONALITY",
      ],
      rows: officers.length
        ? officers.map((row) => ({
            "OFFICER NAME / ADDRESS / CHANGE ADDRESS DATE": joinInline([
              row.OFFICER_NAME,
              row.ADDRESS,
              row.CHANGE_ADDR_DATE,
            ]),
            "IDENTITY NO. / PA REG. NO.": joinInline([row.IDENTITY_ID, row.PA_REG_NO], " "),
            POSITION: ensure(row.POSITION),
            "APPOINTMENT DATE / DISQUALIFIED DATE": joinInline(
              [row.APPMT_DATE, row.DISQUALIFIED_DATE],
              " / ",
            ),
            NATIONALITY: ensure(row.NATIONALITY),
          }))
        : [
            {
              "OFFICER NAME / ADDRESS / CHANGE ADDRESS DATE": "—",
              "IDENTITY NO. / PA REG. NO.": "—",
              POSITION: "—",
              "APPOINTMENT DATE / DISQUALIFIED DATE": "—",
              NATIONALITY: "—",
            },
          ],
    },
    {
      title: "SHAREHOLDERS",
      columns: [
        "SHAREHOLDER NAME / ADDRESS / CHANGE ADDRESS DATE",
        "COMPANY / IDENTITY NO.",
        "COUNTRY OF INCORPORATION",
        "SHARE TYPE",
        "CURRENCY",
        "NOS. OF SHARES",
        "SHARE GROUP",
      ],
      rows: shareholders.length
        ? shareholders.map((row) => ({
            "SHAREHOLDER NAME / ADDRESS / CHANGE ADDRESS DATE": joinInline([
              row.SHAREHOLDER_NAME,
              row.ADDRESS,
              row.CHANGE_ADDR_DATE,
            ]),
            "COMPANY / IDENTITY NO.": ensure(row.COMP_ID),
            "COUNTRY OF INCORPORATION": ensure(row.NATIONALITY),
            "SHARE TYPE": ensure(row.SHARE_TYPE),
            CURRENCY: ensure(row.CURRENCY),
            "NOS. OF SHARES": ensure(row.NO_OF_SHARE),
            "SHARE GROUP": ensure(row.SHARE_GROUP),
          }))
        : [
            {
              "SHAREHOLDER NAME / ADDRESS / CHANGE ADDRESS DATE": "—",
              "COMPANY / IDENTITY NO.": "—",
              "COUNTRY OF INCORPORATION": "—",
              "SHARE TYPE": "—",
              CURRENCY: "—",
              "NOS. OF SHARES": "—",
              "SHARE GROUP": "—",
            },
          ],
    },
    {
      title: "REGISTERED CHARGES",
      columns: [
        "CHARGE NO",
        "CHARGE DATE",
        "CHARGEE(S) COMPANY",
        "CURRENCY",
        "AMOUNT SECURED",
        "STATUS OF SATISFACTION",
      ],
      rows: charges.length
        ? charges.map((row) => ({
            "CHARGE NO": ensure(row.CHARGE_NO),
            "CHARGE DATE": ensure(row.CHARGE_DATE),
            "CHARGEE(S) COMPANY": ensure(row.CHARGEE_COMP),
            CURRENCY: ensure(row.CURRENCY),
            "AMOUNT SECURED": ensure(row.AMT_SECURED),
            "STATUS OF SATISFACTION": ensure(row.STATUS_SATISFACTION),
          }))
        : [
            {
              "CHARGE NO": "Nil",
              "CHARGE DATE": "—",
              "CHARGEE(S) COMPANY": "—",
              CURRENCY: "—",
              "AMOUNT SECURED": "—",
              "STATUS OF SATISFACTION": "—",
            },
          ],
    },
    {
      title: "LIQUIDATOR(S) / RECEIVER(S) / JUDICIAL MANAGER(S)",
      columns: ["NAME / ID NUMBER", "POSITION", "COMPANY", "ADDRESS", "APPOINTMENT DATE"],
      rows: liquidators.length
        ? liquidators.map((row) => ({
            "NAME / ID NUMBER": joinInline([row.LIQ_NAME, row.LIQ_ID], " "),
            POSITION: ensure(row.LIQ_POSITION),
            COMPANY: ensure(row.LIQ_COMP),
            ADDRESS: joinParts([
              row.LIQ_ADDRESS1,
              row.LIQ_ADDRESS2,
              row.LIQ_ADDRESS3,
              row.LIQ_ADDRESS4,
            ]),
            "APPOINTMENT DATE": ensure(row.LIQ_APPOINTMENT_DATE),
          }))
        : [
            {
              "NAME / ID NUMBER": "Nil",
              POSITION: "—",
              COMPANY: "—",
              ADDRESS: "—",
              "APPOINTMENT DATE": "—",
            },
          ],
    },
    {
      title: "LITIGATION",
      columns: [
        "CASE NO.",
        "DATE OF WRIT",
        "STATUS / STATUS DATE",
        "NATURE OF CLAIM / AMOUNT",
        "DEFENDANT STATUS",
        "PLAINTIFF / CLAIMANT STATUS",
      ],
      rows: litigation.length
        ? litigation.map((row) => ({
            "CASE NO.": joinInline([row.COURT_CODE, row.CASE_YEAR, row.CASE_TYPE], " "),
            "DATE OF WRIT": ensure(row.DATE_OF_WRIT),
            "STATUS / STATUS DATE": joinInline([row.CASE_STATUS, row.CASE_STATUS_DATE]),
            "NATURE OF CLAIM / AMOUNT": joinInline([
              row.NATURE_OF_CLAIM,
              joinInline([row.CURRENCY, row.CLAIM_AMOUNT], " "),
            ]),
            "DEFENDANT STATUS": joinInline([
              row.DEF_STATUS,
              row.DEF_STATUS_DATE,
              row.DEF_SOLICITOR,
              row.DEF_LAW_FIRM,
              row.DEF_FILE_REF,
            ]),
            "PLAINTIFF / CLAIMANT STATUS": joinInline([
              row.PLA_STATUS,
              row.PLA_STATUS_DATE,
              row.PLAINTIFF,
              row.PLA_SOLICITOR,
              row.PLA_LAW_FIRM,
              row.PLA_FILE_REF,
            ]),
          }))
        : [{ "CASE NO.": "NO RECORD FOUND" }],
    },
    {
      title: "WINDING UP",
      columns: ["CASE NO. / TYPE", "ACTION DATE", "AMOUNT", "PLAINTIFF"],
      rows: windingUp.length
        ? windingUp.map((row) => ({
            "CASE NO. / TYPE": joinInline([row.COURT_CODE, row.CASE_YEAR, row.CASE_TYPE], " "),
            "ACTION DATE": ensure(row.ACTION_DATE),
            AMOUNT: joinInline([row.CURRENCY1, ensure(row.AMOUNT1, "0.00")], " "),
            PLAINTIFF: ensure(row.PLAINTIFF),
          }))
        : [{ "CASE NO. / TYPE": "NO RECORD FOUND", "ACTION DATE": "—", AMOUNT: "—", PLAINTIFF: "—" }],
    },
  ];
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    color: "#28243a",
    fontFamily: "Helvetica",
    backgroundColor: "#fbfaff",
  },
  header: {
    marginBottom: 18,
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#ded8ff",
    borderStyle: "solid",
    backgroundColor: "#f3f0ff",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    color: "#2a214f",
    fontSize: 17,
    fontWeight: 900,
  },
  headerSubtitle: {
    color: "#6f668d",
    fontSize: 9,
    fontWeight: 700,
    marginTop: 4,
  },
  headerBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    color: "#4f37cc",
    backgroundColor: "#ffffff",
    fontSize: 7,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  section: {
    marginBottom: 14,
  },
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 7,
  },
  sectionAccent: {
    width: 4,
    height: 13,
    borderRadius: 4,
    backgroundColor: "#6d4cff",
  },
  sectionTitleText: {
    color: "#2a214f",
    fontSize: 10.5,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  kvBox: {
    borderWidth: 1,
    borderColor: "#e6e1ff",
    borderStyle: "solid",
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#ffffff",
  },
  kvRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eeeafc",
    borderBottomStyle: "solid",
    paddingVertical: 7,
    paddingHorizontal: 9,
  },
  kvRowAlt: {
    backgroundColor: "#faf9ff",
  },
  kvRowLast: {
    borderBottomWidth: 0,
  },
  kvLabel: {
    width: "40%",
    color: "#6f668d",
    fontSize: 8.5,
    fontWeight: 800,
    paddingRight: 8,
    textTransform: "uppercase",
  },
  kvValue: {
    width: "60%",
    color: "#28243a",
    fontSize: 9.5,
    fontWeight: 700,
  },
  table: {
    borderWidth: 1,
    borderColor: "#e1dcfb",
    borderStyle: "solid",
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#ffffff",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableRowAlt: {
    backgroundColor: "#faf9ff",
  },
  tableHeader: {
    backgroundColor: "#6d4cff",
  },
  tableHeaderCell: {
    color: "#ffffff",
    fontSize: 8.5,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  tableCell: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: "#eeeafc",
    borderRightStyle: "solid",
    borderBottomWidth: 1,
    borderBottomColor: "#f0edfb",
    borderBottomStyle: "solid",
    color: "#28243a",
    fontSize: 8.5,
    lineHeight: 1.35,
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  tableBodyLastCell: {
    borderBottomWidth: 0,
  },
  emptyText: {
    color: "#8d84a8",
    fontStyle: "italic",
  },
  logo: {
    width: 58,
    height: 32,
    objectFit: "contain",
  },
});

const SectionTitle = ({ children }) => (
  <View style={styles.sectionTitleRow}>
    <View style={styles.sectionAccent} />
    <Text style={styles.sectionTitleText}>{children}</Text>
  </View>
);

const KeyValueTable = ({ title, rows }) => (
  <View style={styles.section}>
    {title ? <SectionTitle>{title}</SectionTitle> : null}
    {rows.length ? (
      <View style={styles.kvBox}>
        {rows.map(([label, value], idx) => (
          <View
            key={`${label}-${idx}`}
            style={[
              styles.kvRow,
              idx % 2 === 1 && styles.kvRowAlt,
              idx === rows.length - 1 && styles.kvRowLast,
            ]}
          >
            <Text style={styles.kvLabel}>{ensure(label)}</Text>
            <Text style={styles.kvValue}>{ensure(value)}</Text>
          </View>
        ))}
      </View>
    ) : (
      <Text style={styles.emptyText}>—</Text>
    )}
  </View>
);

const Table = ({ title, columns, rows }) => (
  <View style={styles.section}>
    {title ? <SectionTitle>{title}</SectionTitle> : null}
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        {columns.map((col, idx) => (
          <Text
            key={col}
            style={[
              styles.tableCell,
              styles.tableHeaderCell,
              idx === columns.length - 1 && styles.tableCellLast,
            ]}
          >
            {col}
          </Text>
        ))}
      </View>
      {rows.length ? (
        rows.map((row, rowIdx) => (
          <View
            key={`row-${rowIdx}`}
            style={[styles.tableRow, rowIdx % 2 === 1 && styles.tableRowAlt]}
          >
            {columns.map((col, idx) => (
              <Text
                key={`${col}-${idx}`}
                style={[
                  styles.tableCell,
                  idx === columns.length - 1 && styles.tableCellLast,
                  rowIdx === rows.length - 1 && styles.tableBodyLastCell,
                ]}
              >
                {ensure(row[col])}
              </Text>
            ))}
          </View>
        ))
      ) : (
        <View style={styles.tableRow}>
          <Text style={[styles.tableCell, styles.tableCellLast]}>
            <Text style={styles.emptyText}>—</Text>
          </Text>
        </View>
      )}
    </View>
  </View>
);

const collectObjectKeys = (arr) => {
  const set = new Set();
  arr.forEach((it) => {
    if (it && typeof it === "object" && !Array.isArray(it)) {
      Object.keys(it).forEach((k) => set.add(k));
    }
  });
  return Array.from(set);
};

const renderArraySection = (title, arr, excludedCols) => {
  const items = toArray(arr);
  const sectionTitle = prettyKey(title);

  if (!items.length) {
    return (
      <Table
        title={sectionTitle}
        columns={["RECORD"]}
        rows={[{ RECORD: "—" }]}
      />
    );
  }

  const hasObjects = items.some(
    (it) => it && typeof it === "object" && !Array.isArray(it),
  );
  if (!hasObjects) {
    return (
      <Table
        title={sectionTitle}
        columns={["VALUE"]}
        rows={items.map((v) => ({ VALUE: ensure(v) }))}
      />
    );
  }

  const rawKeys = collectObjectKeys(items);
  const filteredKeys = rawKeys.filter((k) => !excludedCols.has(prettyKey(k)));

  if (!filteredKeys.length) {
    return (
      <Table
        title={sectionTitle}
        columns={["RECORD"]}
        rows={[{ RECORD: "—" }]}
      />
    );
  }

  const columns = filteredKeys.map(prettyKey);
  const rows = items.map((it) => {
    const row = {};
    filteredKeys.forEach((k) => {
      const v = it?.[k];
      if (Array.isArray(v)) {
        row[prettyKey(k)] = v.length ? `${v.length} item(s)` : "—";
      } else if (v && typeof v === "object") {
        row[prettyKey(k)] = Object.keys(v).length ? "[object]" : "—";
      } else {
        row[prettyKey(k)] = ensure(v);
      }
    });
    return row;
  });

  return <Table title={sectionTitle} columns={columns} rows={rows} />;
};

const renderObjectSection = (title, obj, excludedCols) => {
  const entries = Object.entries(obj || {});
  const sectionTitle = prettyKey(title);

  if (!entries.length) {
    return <KeyValueTable title={sectionTitle} rows={[["NO DATA", "—"]]} />;
  }

  const scalarRows = [];
  const arrayEntries = [];
  const objectEntries = [];

  for (const [k, v] of entries) {
    if (Array.isArray(v)) arrayEntries.push([k, v]);
    else if (v && typeof v === "object") objectEntries.push([k, v]);
    else scalarRows.push([prettyKey(k), ensure(v)]);
  }

  return (
    <View style={styles.section}>
      <SectionTitle>{sectionTitle}</SectionTitle>
      {scalarRows.length ? (
        scalarRows.map(([label, value], idx) => (
          <View key={`${label}-${idx}`} style={styles.kvRow}>
            <Text style={styles.kvLabel}>{ensure(label)}</Text>
            <Text style={styles.kvValue}>{ensure(value)}</Text>
          </View>
        ))
      ) : (
        <Text style={styles.emptyText}>—</Text>
      )}
      {objectEntries.map(([k, v]) => (
        <View key={k} wrap={false}>
          {renderObjectSection(k, v, excludedCols)}
        </View>
      ))}
      {arrayEntries.map(([k, v]) => (
        <View key={k} wrap={false}>
          {renderArraySection(k, v, excludedCols)}
        </View>
      ))}
    </View>
  );
};

const renderUnknownSections = (data, excludeKeys, excludedCols) => {
  const keys = Object.keys(data || {}).filter((k) => !excludeKeys.has(k));
  return keys.map((k) => {
    const v = data[k];
    if (Array.isArray(v)) {
      return <View key={k}>{renderArraySection(k, v, excludedCols)}</View>;
    }
    if (v && typeof v === "object") {
      return <View key={k}>{renderObjectSection(k, v, excludedCols)}</View>;
    }
    return (
      <KeyValueTable
        key={k}
        title={prettyKey(k)}
        rows={[[prettyKey(k), ensure(v)]]}
      />
    );
  });
};

const PersonalReport = ({ data, brand, excludedCols }) => {
  const subj = data.SUBJECT_DETAILS || {};

  const addrArr = Array.isArray(data.LIST_ADDRESS)
    ? data.LIST_ADDRESS
    : data.LIST_ADDRESS?.ADDRESS
      ? [data.LIST_ADDRESS.ADDRESS]
      : [];

  const conArr = Array.isArray(data.LIST_CONTACT)
    ? data.LIST_CONTACT
    : data.LIST_CONTACT?.CONTACT
      ? [data.LIST_CONTACT.CONTACT]
      : [];

  const ed = data.LIST_ENQUIRY_DETAILS?.ENQUIRY_DETAILS
    ? [data.LIST_ENQUIRY_DETAILS.ENQUIRY_DETAILS]
    : [];

  const pg = data.PAYMENT_GRADE || {};

  const exclude = new Set([
    "SUBJECT_DETAILS",
    "LIST_ADDRESS",
    "LIST_CONTACT",
    "LIST_ENQUIRY_DETAILS",
    "PAYMENT_GRADE",
    "LIST_COUNTBY_FIN",
    "LIST_COUNTBY_NONFIN",
  ]);

  return (
    <>
      <View
        style={{
          paddingHorizontal: 28,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 6,
          paddingBottom: 6,
        }}
      >
        <Image
          source={brand.logoDataUrl}
          style={{ width: 64, objectFit: "contain" }}
        />
        <View>
          <Text
            style={{
              fontSize: 14,
              fontFamily: "Poppins",
              fontWeight: 700,
            }}
          >
            SmartLend Financial Pte. Ltd.
          </Text>
          <View
            style={{
              flexDirection: "row",
              gap: 4,
              justifyContent: "center",
              alignItems: "center",
              marginTop: -3,
            }}
          >
            <Text style={{ fontSize: 6 }}>UEN: 202327568G</Text>
            <Text style={{ fontSize: 6 }}>Tel: +65 87853373</Text>
        <Text style={{ fontSize: 6 }}>Email: support@smart-lend.com</Text>
          </View>
        </View>
      </View>
      <View
        style={{
          backgroundColor: "lightgrey",
          height: 2,
          width: "100%",
          marginTop: 10,
        }}
      />
      <View
        style={{
          backgroundColor: "#6d4cff",
          height: 3,
          width: "76%",
          marginTop: -3,
          marginLeft: "12%",
          marginBottom: 8,
        }}
      />

      <KeyValueTable
        title="SUBJECT DETAILS"
        rows={[
          ["SUBJECT NAME", subj.SUB_NAME],
          ["ID", `${ensure(subj.SUB_ID_TYPE)} ${ensure(subj.SUB_ID_NO)}`],
          ["NATIONALITY", subj.SUB_NATIONALITY],
          ["DOB", subj.SUB_DOB],
          [
            "MARITAL / GENDER",
            `${ensure(subj.SUB_MARITAL)} / ${ensure(subj.SUB_GENDER)}`,
          ],
        ]}
      />

      <Table
        title="ADDRESS"
        columns={["DATE LOADED", "TYPE", "DETAIL"]}
        rows={
          addrArr.length
            ? addrArr.map((a) => ({
                "DATE LOADED": ensure(a.DATE_LOADED),
                TYPE: ensure(a.ADD_TYPE),
                DETAIL: ensure(a.ADD_DTL),
              }))
            : [{ "DATE LOADED": "—", TYPE: "—", DETAIL: "—" }]
        }
      />

      <Table
        title="CONTACT"
        columns={["DATE LOADED", "TYPE", "DETAIL"]}
        rows={
          conArr.length
            ? conArr.map((c) => ({
                "DATE LOADED": ensure(c.DATE_LOADED),
                TYPE: ensure(c.CON_TYPE),
                DETAIL: ensure(c.CON_DTL),
              }))
            : [{ "DATE LOADED": "—", TYPE: "—", DETAIL: "—" }]
        }
      />

      <Table
        title="ENQUIRY DETAILS"
        columns={["ENQUIRY TYPE", "PRODUCT TYPE", "GUARANTOR", "AMOUNT"]}
        rows={
          ed.length
            ? ed.map((e) => ({
                "ENQUIRY TYPE": ensure(e.ENQUIRY_TYPE),
                "PRODUCT TYPE": ensure(e.PRODUCT_TYPE),
                GUARANTOR: ensure(e.GUARANTOR),
                AMOUNT: ensure(e.CREDIT_AMT),
              }))
            : [
                {
                  "ENQUIRY TYPE": "—",
                  "PRODUCT TYPE": "—",
                  GUARANTOR: "—",
                  AMOUNT: "—",
                },
              ]
        }
      />

      <Table
        title="PAYMENT INFORMATION"
        columns={[
          "STATUS DATE",
          "PAYING RECORD",
          "CREDIT SCORE",
          "TOTAL CREDIT",
          "PAST DUE AMOUNT",
        ]}
        rows={[
          {
            "STATUS DATE": "—",
            "PAYING RECORD": `Score ${ensure(pg.SCORE)} | Current ${ensure(
              pg.CURRENT,
            )} | 30D ${ensure(pg.DAYS_30)} | 60D ${ensure(
              pg.DAYS_60,
            )} | 90D ${ensure(pg.DAYS_90)} | >90D ${ensure(
              pg.MORE_THAN_90,
            )} | Record ${ensure(pg.PAYMENT_RECORD)}`,
            "CREDIT SCORE": ensure(pg.CREDIT_SCORE, "—"),
            "TOTAL CREDIT": "—",
            "PAST DUE AMOUNT": "—",
          },
        ]}
      />

      <Table
        title="SEARCH BY FINANCIAL SECTORS"
        columns={[
          "Year",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ]}
        rows={(data.LIST_COUNTBY_FIN?.COUNTBY_FIN || []).map((r) => ({
          Year: ensure(r.YEAR),
          Jan: ensure(r.MJAN),
          Feb: ensure(r.MFEB),
          Mar: ensure(r.MMAR),
          Apr: ensure(r.MAPR),
          May: ensure(r.MMAY),
          Jun: ensure(r.MJUN),
          Jul: ensure(r.MJUL),
          Aug: ensure(r.MAUG),
          Sep: ensure(r.MSEP),
          Oct: ensure(r.MOCT),
          Nov: ensure(r.MNOV),
          Dec: ensure(r.MDEC),
        }))}
      />

      <Table
        title="SEARCH BY NON-FINANCIAL SECTORS"
        columns={[
          "Year",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ]}
        rows={(data.LIST_COUNTBY_NONFIN?.COUNTBY_NONFIN || []).map((r) => ({
          Year: ensure(r.YEAR),
          Jan: ensure(r.MJAN),
          Feb: ensure(r.MFEB),
          Mar: ensure(r.MMAR),
          Apr: ensure(r.MAPR),
          May: ensure(r.MMAY),
          Jun: ensure(r.MJUN),
          Jul: ensure(r.MJUL),
          Aug: ensure(r.MAUG),
          Sep: ensure(r.MSEP),
          Oct: ensure(r.MOCT),
          Nov: ensure(r.MNOV),
          Dec: ensure(r.MDEC),
        }))}
      />

      {renderUnknownSections(data, exclude, excludedCols)}
    </>
  );
};

const CompanyReport = ({ data, brand, excludedCols }) => {
  const registry = data?.REGISTRY_DETAIL || {};
  const tables = buildCompanyTables(data);
  const exclude = new Set([
    "REGISTRY_DETAIL",
    "GST",
    "LIST_NAME_CHANGE",
    "LIST_CAPITAL",
    "LIST_OFFICER_OWNER",
    "LIST_SHAREHOLDER",
    "LIST_SHARE_INTEREST_COMP",
    "LIST_REG_CHARGES",
    "LIST_LIQUIDATOR",
    "EMPLOYEE_SIZE",
    "LIST_SALES_REVENUE_AMOUNT",
    "COMMERCIAL_PROPERTY_OWNED",
    "LIST_LITIGATION",
    "LIST_WINDING_UP",
    "LIST_COUNTBY_FIN",
    "LIST_COUNTBY_NONFIN",
    "LIST_COUNTBY_LEGAL",
  ]);

  return (
    <>
      <View style={styles.header}>
        {brand.logoDataUrl ? (
          <Image style={styles.logo} src={brand.logoDataUrl} />
        ) : null}
        <View style={styles.headerText}>
          <Text style={styles.headerTitle}>
            {brand.titleLeft || "BIZ CHECK PLUS"}
          </Text>
          <Text style={styles.headerSubtitle}>
            {brand.titleRight || "In collaboration with SCCB"}
          </Text>
        </View>
        <Text style={styles.headerBadge}>Business Intelligence</Text>
      </View>

      <KeyValueTable
        title="COMPANY DETAILS"
        rows={[
          ["COMPANY NAME", registry.COMP_NAME],
          ["REGISTRATION NO.", registry.REG_NO],
        ]}
      />

      <KeyValueTable
        title="REGISTRY DETAIL"
        rows={[
          ["REGISTRATION DATE", registry.REG_DATE],
          ["NAME EFFECTIVE DATE", registry.NAME_EFFECT_DATE],
          ["COUNTRY OF INCORPORATION", registry.COUNTRY_INCORP],
          ["COMPANY TYPE", registry.COMP_TYPE || registry.COMP_TYPE_CODE],
          [
            "REGISTERED ADDRESS",
            joinParts([
              registry.ADDRESS1,
              registry.ADDRESS2,
              registry.ADDRESS3,
              registry.ADDRESS4,
            ]),
          ],
          ["CHANGE ADDRESS DATE", registry.CHANGE_ADDR_DATE],
          ["COMPANY STATUS", registry.COMP_STATUS],
          ["STATUS EFFECTIVE DATE", registry.STATUS_EFFECT_DATE],
          [
            "REGISTERED ACTIVITY 1",
            joinInline([registry.REG_ACTIVITY1, registry.REG_ACTIVITY_DESC1], " "),
          ],
          [
            "REGISTERED ACTIVITY 2",
            joinInline([registry.REG_ACTIVITY2, registry.REG_ACTIVITY_DESC2], " "),
          ],
          ["AUDITOR", registry.AUDITOR],
          ["AUDITOR APPOINTMENT DATE", registry.AUDITOR_APPMT_DATE],
          ["ACCOUNT DATE", registry.ACC_DATE],
          ["DATE OF LAST AR", registry.LAST_AR_DATE],
          ["DATE OF LAST AGM", registry.LAST_AGM_DATE],
        ]}
      />

      {tables.map((table) => (
        <Table
          key={table.title}
          title={table.title}
          columns={table.columns}
          rows={table.rows}
        />
      ))}

      {renderUnknownSections(data, exclude, excludedCols)}
    </>
  );
};

const ReportDocument = ({ reportType, brand, data, excludedCols }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {reportType === "company" ? (
        <CompanyReport data={data} brand={brand} excludedCols={excludedCols} />
      ) : (
        <PersonalReport data={data} brand={brand} excludedCols={excludedCols} />
      )}
    </Page>
  </Document>
);

const SccbPdfViewer = ({
  data,
  reportType = "personal",
  brand = {},
  filename = "Report.pdf",
  excludeColumns = [],
}) => {
  const excludedCols = useMemo(
    () => new Set(excludeColumns.map(prettyKey)),
    [excludeColumns],
  );

  const { reportData, parseError } = useMemo(() => {
    if (!data) return { reportData: null, parseError: null };
    try {
      const parsed = typeof data === "string" ? xmlToJs(data) : data;
      return { reportData: parsed, parseError: null };
    } catch (error) {
      return { reportData: null, parseError: error };
    }
  }, [data]);

  if (!data) {
    return (
      <Box
        sx={{
          padding: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography color="text.secondary">
          No SCCB report available.
        </Typography>
      </Box>
    );
  }

  if (parseError) {
    return (
      <Box
        sx={{
          padding: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography color="error">Unable to parse SCCB report data.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        bgcolor: "white",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          p: 1,
          display: "grid",
          placeItems: "start center",
        }}
      >
        <PDFViewer
          style={{
            width: "100%",
            height: "78vh",
            border: "none",
            borderRadius: "20px",
          }}
        >
          <ReportDocument
            reportType={reportType}
            brand={brand}
            data={reportData}
            excludedCols={excludedCols}
          />
        </PDFViewer>
      </Box>
    </Box>
  );
};

export default SccbPdfViewer;
